import { useState, useEffect } from 'react';
import { KeyRound, AlertCircle, Loader2, CheckCircle2, ShieldX } from 'lucide-react';
import { api } from '../../api/client';
import {
    signInWithGoogle,
    signInWithMicrosoft,
    type OAuthProvider,
} from '../../utils/oauthRecovery';
import {
    deriveKeyFromOAuthSub,
    decryptPrivateKey,
    STORAGE_KEY,
} from '../../utils/crypto';
import './Auth.css';

interface Props {
    onRecovered: () => void; // Called when keys are restored — move to set-new-password step
    onBack: () => void;
}

interface OAuthConfig {
    google_client_id?: string;
    microsoft_client_id?: string;
    microsoft_tenant_id?: string;
}

type RecoveryState = 'idle' | 'recovering' | 'success' | 'no-provider' | 'error';

export default function OAuthRecoveryPage({ onRecovered, onBack }: Props) {
    const [config, setConfig] = useState<OAuthConfig | null>(null);
    const [state, setState] = useState<RecoveryState>('idle');
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [activeProvider, setActiveProvider] = useState<OAuthProvider | null>(null);

    useEffect(() => {
        api.getOAuthConfig()
            .then(setConfig)
            .catch(() => setConfig({}));
    }, []);

    const handleRecover = async (provider: OAuthProvider) => {
        setErrorMsg(null);
        setState('recovering');
        setActiveProvider(provider);

        try {
            // 1. Trigger OAuth popup
            let identity;
            if (provider === 'google') {
                identity = await signInWithGoogle(config!.google_client_id!);
            } else {
                identity = await signInWithMicrosoft(
                    config!.microsoft_client_id!,
                    config!.microsoft_tenant_id || 'common'
                );
            }

            // 2. Send id_token to server — server verifies and returns the encrypted passkey blob
            const { enc_private_key_passkey, key_salt_passkey, public_key } =
                await api.oauthRecovery({ provider, id_token: identity.id_token });

            // 3. Derive the wrapping key from the stable OAuth sub + stored salt
            const wrappingKey = await deriveKeyFromOAuthSub(identity.sub, provider, key_salt_passkey);

            // 4. Decrypt the private key — this proves the identity matches
            const privateKey = await decryptPrivateKey(enc_private_key_passkey, wrappingKey);

            // 5. Store recovered keys in localStorage — AppContext will migrate this to a user-specific key on next login
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ publicKey: public_key, privateKey }));

            // Helpful for debugging cross-tab issues
            console.info('[E2EE] Keys recovered via OAuth. Stored in legacy slot for migration.');

            setState('success');
            setTimeout(() => onRecovered(), 1500);
        } catch (err: any) {
            const msg =
                err?.response?.data?.error ||
                err?.message ||
                'Recovery failed. Please try again or contact your administrator.';

            if (msg.includes('No recovery account')) {
                setState('no-provider');
            } else {
                setState('error');
            }
            setErrorMsg(msg);
        } finally {
            setActiveProvider(null);
        }
    };

    if (!config) {
        return (
            <div className="auth-container">
                <div className="auth-card" style={{ textAlign: 'center' }}>
                    <Loader2 size={32} className="spin" style={{ margin: '32px auto' }} />
                </div>
            </div>
        );
    }

    const hasGoogle = !!config.google_client_id;
    const hasMicrosoft = !!config.microsoft_client_id;

    return (
        <div className="auth-container">
            <div className="auth-card">
                <div className="auth-header">
                    <div className="auth-logo" style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>
                        <KeyRound size={28} />
                    </div>
                    <h1>Recover Encryption Key</h1>
                    <p>Sign in with your linked account to restore access to your encrypted messages.</p>
                </div>

                {state === 'success' && (
                    <div style={{ textAlign: 'center', padding: '12px 0 24px', color: '#4ade80' }}>
                        <CheckCircle2 size={40} style={{ margin: '0 auto 8px' }} />
                        <p style={{ fontWeight: 600 }}>Key recovered! Setting up new password…</p>
                    </div>
                )}

                {(state === 'error' || state === 'no-provider') && errorMsg && (
                    <div className="auth-error" style={{ marginBottom: 16 }}>
                        {state === 'no-provider' ? <ShieldX size={16} /> : <AlertCircle size={16} />}
                        <span>{errorMsg}</span>
                    </div>
                )}

                {state !== 'success' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {!hasGoogle && !hasMicrosoft && (
                            <div className="auth-error">
                                <ShieldX size={16} />
                                <span>No OAuth recovery providers have been configured. Please contact your administrator.</span>
                            </div>
                        )}

                        {hasGoogle && (
                            <button
                                type="button"
                                className="auth-oauth-btn"
                                onClick={() => handleRecover('google')}
                                disabled={state === 'recovering'}
                            >
                                {activeProvider === 'google' ? (
                                    <Loader2 size={18} className="spin" />
                                ) : (
                                    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                                        <path fill="#4285F4" d="M43.6 20.3H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 2.9l5.7-5.7C34.5 6.5 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.5-.4-3.7z" />
                                        <path fill="#34A853" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 8 2.9l5.7-5.7C34.5 7.2 29.6 5 24 5 16.3 5 9.7 9 6.3 14.7z" />
                                        <path fill="#FBBC05" d="M24 43c5.4 0 10.2-1.9 13.8-5.1l-6.4-5.2C29.3 34.7 26.8 35.9 24 35.9c-5.2 0-9.5-3.3-11.1-8l-6.5 5C9.8 39.6 16.4 43 24 43z" />
                                        <path fill="#EA4335" d="M43.6 20.3H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.4 5.2C40.5 36.4 44 30.6 44 24c0-1.3-.1-2.5-.4-3.7z" />
                                    </svg>
                                )}
                                Recover with Google
                            </button>
                        )}

                        {hasMicrosoft && (
                            <button
                                type="button"
                                className="auth-oauth-btn"
                                onClick={() => handleRecover('microsoft')}
                                disabled={state === 'recovering'}
                            >
                                {activeProvider === 'microsoft' ? (
                                    <Loader2 size={18} className="spin" />
                                ) : (
                                    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden>
                                        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                                        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                                        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                                        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                                    </svg>
                                )}
                                Recover with Microsoft
                            </button>
                        )}

                        <button type="button" className="auth-link" onClick={onBack}>
                            ← Back to login
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
